import { useState } from 'react'
import {
	EuiButton,
	EuiFormRow,
	EuiModalBody,
	EuiModalFooter,
	EuiTable,
	EuiTableHeader,
	EuiTableHeaderCell,
	EuiTableBody,
	EuiTableRow,
	EuiTableRowCell,
	EuiFieldNumber,
} from '@equipmentshare/ds2'

import validateForm from './helpers/validateForm'

import { ItemStatus, Transaction, TransactionItem } from '../../../../../../graphql'

export type ReceivePartsFormProps = {
  closeForm: () => void,
	completeTransaction: ( transactionId: any, transactionItems: any ) => Promise<void>,
	transaction: Transaction,
}

export type ItemIdToQuantity = {
	[ key: number ]: string,
}

export type Validation = {
	isInvalid: boolean,
	error?: string,
}

export type ItemIdToValidation = {
	[ key: number ]: Validation,
}

export function initializeItemQuantities( transaction: Transaction ): ItemIdToQuantity {
	const items = transaction.transactionItems as TransactionItem[]
	const itemQuantities: ItemIdToQuantity = {}
	items.forEach( ( item ) => {
		itemQuantities[ item.id ] = ''
	} )

	return itemQuantities
}

export function initializeItemValidation( transaction: Transaction ): ItemIdToValidation {
	const items = transaction.transactionItems as TransactionItem[]
	const itemValidation: ItemIdToValidation = {}
	items.forEach( ( item ) => {
		itemValidation[ item.id ] = { isInvalid: false }
	} )

	return itemValidation
}

export default function ReceivePartsForm( { closeForm, completeTransaction, transaction }: ReceivePartsFormProps ) {
	const [ itemQuantities, setItemQuantities ] = useState<ItemIdToQuantity>( initializeItemQuantities( transaction ) )
	const [ itemValidation, setItemValidation ] = useState<ItemIdToValidation>( initializeItemValidation( transaction ) )
	const [ isSaving, setIsSaving ] = useState( false )
	const items = transaction.transactionItems as TransactionItem[]

	const onChangeQuantity = ( itemId: number ) => ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const quantity = event.currentTarget.value
		const newQuantities = {
			...itemQuantities,
			[ itemId ]: quantity,
		}
		const { newItemValidation } = validateForm( items, newQuantities )

		setItemQuantities( newQuantities )
		setItemValidation( newItemValidation )
	}

	const onSubmitClick = async() => {
		if ( Object.values( itemQuantities ).every( ( q ) => !q || q === '0' ) ) {
			closeForm()
		} else {
			setIsSaving( true )
			const { isInvalid, newItemValidation } = validateForm( items, itemQuantities )
			if ( !isInvalid ) {
				try {
					const itemsToSubmit = items.filter( ( item ) => !!itemQuantities[ item.id ] && itemQuantities[ item.id ] !== '0' )
					await completeTransaction( transaction.id, itemsToSubmit.map( ( item ) => ( {
						transactionItemId: item.id,
						quantityReceived: parseInt( itemQuantities[ item.id ], 10 ),
						itemStatus: ItemStatus.Received,
					} ) ) )
					closeForm()
				} catch ( e ) {
					setIsSaving( false )
				}
			} else {
				setItemValidation( newItemValidation )
				setIsSaving( false )
			}
		}
	}

	return (
		<>
			<EuiModalBody>
				<EuiTable tableLayout="auto">
					<EuiTableHeader>
						<EuiTableHeaderCell>Item</EuiTableHeaderCell>
						<EuiTableHeaderCell>Mfr #</EuiTableHeaderCell>
						<EuiTableHeaderCell>Qty Ordered</EuiTableHeaderCell>
						<EuiTableHeaderCell>Qty Received</EuiTableHeaderCell>
					</EuiTableHeader>
					<EuiTableBody>
						{ items.map( ( item ) => {
							// Completing splits/consolidates items into new rows (new IDs); the mutation result
							// updates this transaction in the cache before the modal closes
							const validation = itemValidation[ item.id ] ?? { isInvalid: false }

							return (
								<EuiTableRow key={`item-row-${item.id}`}>
									<EuiTableRowCell valign="baseline">{item.part?.partClass.description}</EuiTableRowCell>
									<EuiTableRowCell valign="baseline">{item.part?.partNumber}</EuiTableRowCell>
									<EuiTableRowCell valign="baseline">{item.quantityOrdered}</EuiTableRowCell>
									<EuiTableRowCell valign="baseline">
										<EuiFormRow
											display="rowCompressed"
											isInvalid={validation.isInvalid}
											error={validation.error}
										>
											<EuiFieldNumber
												value={itemQuantities[ item.id ] ?? ''}
												onChange={onChangeQuantity( item.id )}
												compressed
												isInvalid={validation.isInvalid}
											/>
										</EuiFormRow>
									</EuiTableRowCell>
								</EuiTableRow>
							)
						} ) }
					</EuiTableBody>
				</EuiTable>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					color="text"
					onClick={closeForm}
				>
          Cancel
				</EuiButton>
				<EuiButton
					id="submitBtn"
					isLoading={isSaving}
					onClick={onSubmitClick}
				>
					Receive items
				</EuiButton>
			</EuiModalFooter>
		</>

	)
}
